/* eslint-disable react/prop-types */
import React, {
  useEffect, useLayoutEffect, useRef, useState, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import DraggableContent from './DraggableContent';
import { flushDebounceForTable } from './hooks/debounceHandler';
import PORTAL_SELECTORS from '../../constants/portalSelectors';
import { setActiveExerciseId } from '../../ducks/calendarActions';

const POPOVER_WIDTH = 600;
const SCROLL_THRESHOLD = 10;
const ANCHOR_GAP = 8;
const VIEWPORT_PADDING = 8;

const PopoverContainer = styled('div')`
  position: fixed;
  width: ${POPOVER_WIDTH}px;
  /* Narrow viewports (e.g. laptop with sidebar open) can't comfortably
   * host 600px; drop to 500px below 1100px. computeInitialPosition reads
   * offsetWidth after paint so the flip/clamp logic stays in sync. */
  @media (max-width: 1100px) { width: 500px; }
  max-height: 80vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 0 1px rgba(0, 0, 0, 0.25);
  padding: 12px;
  /* Below rc-dropdown (1070), rc-trigger (1050), and ReactModal contents
   * (1100) so any menus/modals opened from inside the popover stack on
   * top instead of underneath. Above the calendar grid which uses no
   * z-index in the default flow. */
  z-index: 1000;
  /* The shell takes focus on open so Tab enters the table (see the focus
   * effect below). The border and shadow already read as a dialog, so a
   * focus ring around a 600px panel would look like a defect. */
  &:focus { outline: none; }
`;

const Footer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const BackToDay = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.hover};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover { opacity: 0.75; }
`;

const DateLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/**
 * Edit popover for a month-view exercise. Anchors to the clicked row
 * (Google Calendar style), repositioning on scroll/resize. Renders the
 * full week/day editor (`DraggableContent`) inside without the rbd
 * `Draggable` wrapper, so dragging stays scoped to the underlying
 * month-view item; the popover is for editing only.
 */
const MonthEditPopover = ({
  content,
  index,
  date,
  anchorEl,
  onClose,
  scrollContainerRef,
}) => {
  const dispatch = useDispatch();
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [hasScrolledAway, setHasScrolledAway] = useState(false);
  const initialScrollRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const dragIsInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );
  const activeModal = useSelector((state) => state.calendar.data.activeModal);
  const activeSecondLayerModal = useSelector(
    (state) => state.calendar.data.activeSecondLayerModal,
  );

  const [localAdditionalInfo, setLocalAdditionalInfo] = useState('');
  const [localDescription, setLocalDescription] = useState('');

  const computeInitialPosition = useCallback(() => {
    if (!anchorEl) return null;
    const rect = anchorEl.getBoundingClientRect();
    /** detached or zero-sized — bail */
    if (rect.width === 0 && rect.height === 0) return null;

    const popoverEl = popoverRef.current;
    const measuredHeight = popoverEl
      ? Math.min(popoverEl.offsetHeight, window.innerHeight * 0.8)
      : 300;
    /** Read the actual rendered width so the media-query responsive size
     * (500px below 1100px, 600px above) is reflected in the flip/clamp. */
    const measuredWidth = popoverEl ? popoverEl.offsetWidth : POPOVER_WIDTH;

    /** Prefer right of anchor; flip to left if not enough room. */
    let left = rect.right + ANCHOR_GAP;
    if (left + measuredWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = rect.left - measuredWidth - ANCHOR_GAP;
    }
    /** If neither side fits, clamp to viewport right edge. */
    if (left < VIEWPORT_PADDING) {
      left = Math.max(VIEWPORT_PADDING, window.innerWidth - measuredWidth - VIEWPORT_PADDING);
    }

    /** Vertically align top with anchor, clamp inside viewport. */
    let { top } = rect;
    if (top + measuredHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = window.innerHeight - measuredHeight - VIEWPORT_PADDING;
    }
    if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;

    return { top, left };
  }, [anchorEl]);

  /** Initial measurement after first paint so popover height is known.
   * Position is computed ONCE — the popover stays put after opening so
   * coaches can scroll the calendar and compare exercises without the
   * popover chasing the row off-screen. */
  useLayoutEffect(() => {
    setPosition(computeInitialPosition());
  }, [computeInitialPosition, content?.assignedId]);

  /** Re-clamp into the viewport on window resize so the popover
   * doesn't drift off-screen when the window shrinks. Does NOT
   * re-anchor — keeps the popover where it was opened. */
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => {
        if (!prev) return prev;
        const popoverEl = popoverRef.current;
        const measuredHeight = popoverEl
          ? Math.min(popoverEl.offsetHeight, window.innerHeight * 0.8)
          : 300;
        const measuredWidth = popoverEl ? popoverEl.offsetWidth : POPOVER_WIDTH;
        let { top, left } = prev;
        const maxLeft = window.innerWidth - measuredWidth - VIEWPORT_PADDING;
        const maxTop = window.innerHeight - measuredHeight - VIEWPORT_PADDING;
        if (left > maxLeft) left = Math.max(VIEWPORT_PADDING, maxLeft);
        if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
        if (top > maxTop) top = Math.max(VIEWPORT_PADDING, maxTop);
        if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;
        if (top === prev.top && left === prev.left) return prev;
        return { top, left };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** When popover content grows (e.g. sets added), re-clamp the top
   * so the bottom edge stays inside the viewport. The popover only
   * moves UP — it never jumps back down once it has been pushed up. */
  useEffect(() => {
    const el = popoverRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      setPosition((prev) => {
        if (!prev) return prev;
        const maxTop = window.innerHeight - el.offsetHeight - VIEWPORT_PADDING;
        const clampedTop = Math.max(VIEWPORT_PADDING, maxTop);
        if (prev.top <= clampedTop) return prev;
        return { ...prev, top: clampedTop };
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /** Month view's editor is a portal, so the click that opened it landed on
   * the month row — not on DraggableContent, whose onClickCapture is the only
   * thing that marks an item active. Without this every per-item shortcut
   * (⌘+O options, ⌘+I info, ⌘+D delete, ⌘+P add property) no-ops until the
   * coach clicks inside the popover. CalendarContent's outside-click handler
   * clears the id on the same click's mousedown, which runs before this mount
   * effect, so this wins.
   *
   * Deliberately no cleanup: once the popover unmounts there is no
   * CalendarItemDropdownOptions listening for this assignedId, so a leftover
   * id is inert. MonthView keys the popover by assignedId, so switching
   * exercises remounts and re-dispatches. */
  useEffect(() => {
    if (!content?.assignedId) return;
    dispatch(setActiveExerciseId(content.assignedId));
  }, [dispatch, content?.assignedId]);

  /** Portal'd to the end of document.body, so the click that opened the
   * popover leaves the browser's sequential-focus starting point back in the
   * month grid — Tab would walk the rest of the grid instead of entering the
   * table. Focus the shell rather than an input so Tab steps into the table
   * and Shift+Tab steps back out, matching day/week without dropping a caret
   * into a cell.
   *
   * Gated on `position` because the container renders visibility: hidden
   * until it's measured, and a hidden element can't take focus. hasFocusedRef
   * keeps it to once: `position` also changes on window resize and on the
   * ResizeObserver re-clamp when sets are added, and without the guard that
   * would yank focus out of the cell the coach is typing in. */
  useEffect(() => {
    if (!position || hasFocusedRef.current) return;
    const el = popoverRef.current;
    if (!el) return;
    hasFocusedRef.current = true;
    el.focus({ preventScroll: true });
  }, [position]);

  /** Esc to close — unless a modal opened from inside the popover is up.
   * react-modal only stops the Escape when focus is inside the modal, so
   * without this guard an Escape aimed at the modal collapses the whole
   * entry behind it and leaves the modal open. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (activeModal || activeSecondLayerModal) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, activeModal, activeSecondLayerModal]);

  /** Enter commits the entry, mirroring the table's SAVE button. The popover is
   * a portal and focus starts on the shell (a div), so native implicit
   * submission only fires when the caret happens to sit in a plain input —
   * otherwise Enter no-ops, coaches read that as "saved", then click away and
   * lose the row (closePopover discards unsaved preSave items). */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Enter') return;
      /** Shift+Enter is the picker's "create new exercise" shortcut; leave
       * every modified Enter to whoever owns it. */
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      /** IME composition commits with Enter — never a save. */
      if (e.isComposing || e.keyCode === 229) return;
      /** Auto-repeat would fire one submit per tick: handleSaveCalendarExercise
       * has no in-flight guard, so each one POSTs and the duplicate responses
       * get dropped on the floor while the server keeps the rows. */
      if (e.repeat) return;
      /** A modal opened from inside the popover owns Enter. */
      if (activeModal || activeSecondLayerModal) return;

      const root = popoverRef.current;
      if (!root || !root.contains(e.target)) return;

      const { tagName } = e.target;
      /** Preserve newlines, and let a focused button activate itself. */
      if (tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (tagName === 'BUTTON' || e.target.closest?.('button')) return;
      /** Interactions inside portaled UI the popover spawned (dropdowns,
       * tooltips, submenus) aren't saves — same list the outside-click
       * check uses. */
      if (e.target.closest?.(PORTAL_SELECTORS)) return;
      /** An open react-select menu means Enter picks the focused option. The
       * exercise picker portals its menu to document.body, so this has to be a
       * global query rather than one scoped to `root`. */
      if (document.querySelector('[class*="__menu"]')) return;

      /** Only the preSave tables render a form + submit button; saved-item
       * tables auto-save through the debounce instead. */
      const form = root.querySelector('form');
      if (!form && !content?.assignedId) return;

      e.preventDefault();

      /** A mouse click on SAVE blurs the focused cell on the way down, and those
       * onBlur handlers are load-bearing: they normalize values (an emptied Sets
       * field back to 1 — otherwise Array.from({ length: '' }) yields zero sets)
       * and set the field errors handleSaveCalendarExercise gates on. Blur first
       * so Enter commits the same corrected state a click would.
       *
       * Synchronous is enough: blur() dispatches through React's own document
       * listener, so the resulting setState is flushed before it returns and
       * requestSubmit() picks up the latest render's onSubmit closure. Deferring
       * to a timeout/rAF instead would let the popover unmount in between. */
      const active = document.activeElement;
      if (active && active !== root && root.contains(active) && typeof active.blur === 'function') {
        active.blur();
      }

      if (form) {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          /** Safari < 16 has no requestSubmit. React 16 delegates at the
           * document, so a bubbling native submit still reaches onSubmit. */
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      } else {
        flushDebounceForTable(content.assignedId);
      }

      /** The blur left focus on <body>. Hand it back to the shell or a second
       * Enter fails the root.contains(e.target) check — which matters whenever
       * the popover survives, i.e. the saved-item path and a preSave save that
       * was rejected by validation. */
      if (popoverRef.current) popoverRef.current.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeModal, activeSecondLayerModal, content?.assignedId]);

  /** Outside click. Clicks on the anchor row are ignored — the same-row
   * case is harmless (parent re-sets the same item) and clicking
   * another row re-anchors via parent state. Clicks on portal'd UI
   * (dropdowns, modals, react-select menus) the popover spawned are
   * also ignored so opening them doesn't dismiss the popover. */
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      /** Skip if the click landed in a dropdown / modal / tooltip
       * portal'd above the popover — those are interactions the user
       * triggered FROM the popover and should not dismiss it. */
      if (e.target.closest && e.target.closest(PORTAL_SELECTORS)) return;
      onClose();
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [anchorEl, onClose]);

  /** Close if a drag starts — the popover's anchor would otherwise
   * float along with the dragged ghost and editing during a drag
   * doesn't make sense. */
  useEffect(() => {
    if (dragIsInProgress) onClose();
  }, [dragIsInProgress, onClose]);

  /** Track whether the coach has scrolled the calendar away from the
   * position it was at when the popover opened, so we can show the
   * "Back to day" link when the anchor row is off-screen. */
  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return undefined;
    initialScrollRef.current = { top: el.scrollTop, left: el.scrollLeft };
    const onScroll = () => {
      const { top, left } = initialScrollRef.current;
      const moved = Math.abs(el.scrollTop - top) > SCROLL_THRESHOLD
        || Math.abs(el.scrollLeft - left) > SCROLL_THRESHOLD;
      setHasScrolledAway(moved);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollContainerRef]);

  return createPortal(
    <PopoverContainer
      ref={popoverRef}
      role='dialog'
      /** Marks this subtree as the month editor so useEnterSavesForm stands
       * down for the table hosted here — this component owns Enter inside the
       * popover, including focus on the shell. An explicit attribute rather
       * than role='dialog', which react-modal also sets at runtime. */
      data-month-edit-popover
      tabIndex={-1}
      aria-label={content?.title ? `Edit exercise: ${content.title}` : 'Edit exercise'}
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      <DraggableContent
        content={content}
        index={index}
        date={date}
        isHovered={false}
        isDragging={false}
        isExpanded
        setIsExpanded={() => {}}
        checkboxVisible={false}
        omitCheckbox
        isWeekView={false}
        shouldShowQuickActions
        measure={() => {}}
        onPopoverClose={onClose}
        localAdditionalInfo={localAdditionalInfo}
        setLocalAdditionalInfo={setLocalAdditionalInfo}
        localDescription={localDescription}
        setLocalDescription={setLocalDescription}
      />
      {date && (
        <Footer>
          {hasScrolledAway ? (
            <BackToDay
              onClick={() => {
                const el = scrollContainerRef?.current;
                if (!el || !initialScrollRef.current) return;
                el.scrollTo({
                  top: initialScrollRef.current.top,
                  left: initialScrollRef.current.left,
                  behavior: 'smooth',
                });
              }}
            >
              ← Back to day
            </BackToDay>
          ) : <span />}
          <DateLabel>{moment(date).format('ddd, MMM D, YYYY')}</DateLabel>
        </Footer>
      )}
    </PopoverContainer>,
    document.body,
  );
};

export default MonthEditPopover;
